import React, { useState, useEffect } from 'react';
import { getApiUrl } from "@/config/appConfig";
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Gift, Copy, Share2, Users, Clock, CheckCircle, 
  DollarSign, ExternalLink, AlertCircle, Wallet,
  Twitter, Linkedin, Facebook, Mail, Calendar
} from 'lucide-react';

const API = getApiUrl();

export default function ReferralDashboard() {
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [creditData, setCreditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [codeRes, referralsRes, creditRes] = await Promise.all([
          axios.get(`${API}/referrals/my-code`, { withCredentials: true }),
          axios.get(`${API}/referrals/my-referrals`, { withCredentials: true }),
          axios.get(`${API}/referrals/my-credit`, { withCredentials: true })
        ]);
        if (isMounted) {
          setReferralData(codeRes.data);
          setReferrals(referralsRes.data.referrals || []);
          setCreditData(creditRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch referral data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);  // Empty dependency array - only fetch once on mount

  const copyToClipboard = async () => {
    if (referralData?.referral_url) {
      await navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnSocial = (platform) => {
    const url = encodeURIComponent(referralData?.referral_url || '');
    const text = encodeURIComponent(`I'm using SeatPilot for my event ticketing! Sign up with my link and we both get £${referralData?.reward_amount || 50} credit!`);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      email: `mailto:?subject=Try SeatPilot&body=${text}%20${url}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
          <p className="text-gray-500 mt-1">Refer friends and earn credit for White-Label subscription</p>
        </div>
        <Badge className="bg-violet-100 text-violet-700 text-lg px-4 py-1">
          £{referralData?.reward_amount || 50} per referral
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Referrals</p>
                <p className="text-2xl font-bold">{referralData?.stats?.total_referrals || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold">{referralData?.stats?.pending_referrals || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Confirmed</p>
                <p className="text-2xl font-bold">{referralData?.stats?.confirmed_referrals || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Wallet className="h-5 w-5 text-violet-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Available Credit</p>
                <p className="text-2xl font-bold">£{(creditData?.available_credit || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Credit Info */}
      {creditData?.available_credit > 0 && (
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-violet-900 flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Your Referral Credit
                </h3>
                <p className="text-3xl font-bold text-violet-600 mt-2">
                  £{creditData.available_credit.toFixed(2)}
                </p>
                <p className="text-sm text-violet-700 mt-1">
                  = {creditData.months_covered} months of White-Label free!
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-violet-600">Expires:</p>
                <p className="font-medium text-violet-800">
                  {formatDate(creditData.credit_expiry_date)}
                </p>
                <p className="text-xs text-violet-500 mt-1">
                  Use for White-Label only
                </p>
              </div>
            </div>
            
            {creditData.credit_expiry_date && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">Credit expires in 6 months if unused</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Referral Link */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Your Referral Link
          </CardTitle>
          <CardDescription>
            Share this link with friends. When they subscribe to a paid plan, you both get £{referralData?.reward_amount || 50} credit!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={referralData?.referral_url || ''}
              readOnly
              className="flex-1 font-mono text-sm bg-gray-50"
            />
            <Button onClick={copyToClipboard} variant={copied ? "default" : "outline"}>
              {copied ? <CheckCircle className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>

          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Share on:</p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => shareOnSocial('twitter')}
                className="text-blue-500 hover:bg-blue-50"
              >
                <Twitter className="h-4 w-4 mr-1" /> Twitter
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => shareOnSocial('linkedin')}
                className="text-blue-700 hover:bg-blue-50"
              >
                <Linkedin className="h-4 w-4 mr-1" /> LinkedIn
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => shareOnSocial('facebook')}
                className="text-blue-600 hover:bg-blue-50"
              >
                <Facebook className="h-4 w-4 mr-1" /> Facebook
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => shareOnSocial('email')}
                className="text-gray-600 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-1" /> Email
              </Button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Your Referral Code</h4>
            <code className="text-lg font-mono font-bold text-violet-600 bg-violet-100 px-3 py-1 rounded">
              {referralData?.referral_code || '---'}
            </code>
          </div>
        </CardContent>
      </Card>

      {/* How it Works */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-violet-600">1</span>
              </div>
              <h4 className="font-medium mb-1">Share Your Link</h4>
              <p className="text-sm text-gray-500">Send your unique referral link to friends and colleagues</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-violet-600">2</span>
              </div>
              <h4 className="font-medium mb-1">They Subscribe</h4>
              <p className="text-sm text-gray-500">When they sign up and subscribe to any paid plan</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-violet-600">3</span>
              </div>
              <h4 className="font-medium mb-1">Both Get £{referralData?.reward_amount || 50}</h4>
              <p className="text-sm text-gray-500">Credit for White-Label subscription (2.5 months free!)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle>Your Referrals</CardTitle>
          <CardDescription>Track the status of people you&apos;ve referred</CardDescription>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No referrals yet. Share your link to start earning!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Referred</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Date</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-500">Reward</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.referral_id} className="border-b last:border-0">
                      <td className="py-3 px-2">
                        <span className="font-medium">{ref.referred_email_masked || ref.referred_email}</span>
                      </td>
                      <td className="py-3 px-2 text-gray-500">
                        {formatDate(ref.created_at)}
                      </td>
                      <td className="py-3 px-2">
                        {ref.status === 'pending' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <Clock className="h-3 w-3 mr-1" /> Pending
                          </Badge>
                        )}
                        {ref.status === 'confirmed' && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" /> Confirmed
                          </Badge>
                        )}
                        {ref.status === 'rejected' && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            Rejected
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {ref.status === 'confirmed' ? (
                          <span className="font-medium text-green-600">+£{ref.reward_amount}</span>
                        ) : ref.status === 'pending' ? (
                          <span className="text-gray-400">£{ref.reward_amount} (pending)</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credit Transactions */}
      {creditData?.transactions?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Credit History</CardTitle>
            <CardDescription>Your referral credit transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {creditData.transactions.slice().reverse().map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{tx.description}</p>
                    <p className="text-sm text-gray-500">{formatDate(tx.timestamp)}</p>
                  </div>
                  <span className={`font-medium ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.type === 'credit' ? '+' : '-'}£{Math.abs(tx.amount).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
